import BasePage from "../components/BasePage";
import Hero from "../components/Hero";
import { motion } from "framer-motion";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";
import TokenImageNAU from "../components/TokenImageNAU";
import TokenImageNAUN from "../components/TokenImageNAUN";
import TokenImageNAUY from "../components/TokenImageNAUY";

const HomePage: React.FC = () => {
  return (
    <BasePage
      title="UcWaves - Decentralized Opinion Sharing"
      description="UcWaves is an innovative decentralized opinion sharing platform on Base, enabling transparent, immutable, and community-driven opinion exchange."
      keywords="UcWaves, decentralized, blockchain, opinion sharing, Base, Ethereum"
    >
      {/* Hero full screen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          type: "spring",
          stiffness: 200,
          damping: 20,
        }}
        viewport={{ once: true }}
      >
        <Hero />
      </motion.div>

      {/* Section Features with decorative images */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
        {/* Decorative image left */}
        <img
          src={imgL}
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute left-4 md:left-16 xl:left-32  top-5 transform -translate-y-5  w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
        />

        {/* Decorative image right */}
        <img
          src={imgR}
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
        />

        {/* Content wrapper to ensure above images */}
        <div className="relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="flex items-center justify-center"
          >
            <h1 className="text-6xl sm:text-6xl md:text-[52px] md:mt-15 font-bold text-secondary-light text-center  shadow-red-700 mb-32 ">
              The North American Union
            </h1>
          </motion.div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
            type: "spring",
            stiffness: 200,
            damping: 20,
          }}
          viewport={{ once: true }}
          className="text-center text-primary-dark text-2xl sm:text-5xl font-bold mt-10 mb-10"
        >
          A New Era of Economic, Military and Intellectual Might
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
            type: "spring",
            stiffness: 200,
            damping: 20,
          }}
          viewport={{ once: true }}
          className="text-gray-700 max-w-6xl mx-auto mb-16 text-base sm:text-lg md:text-xl text-justify"
        >
          In an increasingly interconnected world, the concept of a North
          American Union (NAU) between the United States and Canada emerges as a
          transformative vision. This proposed union aims to introduce a shared
          currency, facilitate seamless trade, and establish freedom of movement
          for workers, all while preserving the national sovereignty of both
          countries. The economic and social benefits of such an alliance would
          be profound, reshaping the landscape of North America to enhance
          prosperity, innovation, and global competitiveness.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="p-6 bg-white shadow-sm border border-[var(--color-secondary-muted)] rounded-md"
          >
            <h4 className="text-xl font-semibold text-secondary mb-2">
              1. A Unified Currency for Economic Stability
            </h4>
            <p className="text-gray-700 text-sm sm:text-base text-justify">
              Similar to how the Euro strengthened economic ties in Europe, a
              North American currency could stabilize markets, reduce exchange
              rate fluctuations, and simplify cross-border transactions. This
              financial integration would enhance trade efficiency, encourage
              investment, and bolster economic resilience against global
              economic fluctuations.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="p-6 bg-white shadow-sm border border-[var(--color-secondary-muted)] rounded-md"
          >
            <h4 className="text-xl font-semibold text-secondary mb-2">
              2. Seamless Trade & Business Growth
            </h4>
            <p className="text-gray-700 text-sm sm:text-base text-justify">
              Building upon agreements like NAFTA and its successor, USMCA, a
              full economic union would remove remaining barriers, eliminate
              tariffs, and enable businesses to expand freely across borders.
              Entrepreneurs and corporations would benefit from a larger,
              unified market, fostering innovation and job creation.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="p-6 bg-white shadow-sm border border-[var(--color-secondary-muted)] rounded-md"
          >
            <h4 className="text-xl font-semibold text-secondary mb-2">
              3. Freedom to Work & Live Across Borders
            </h4>
            <p className="text-gray-700 text-sm sm:text-base text-justify">
              One of the most significant advantages would be the ability for
              citizens of both countries to live and work freely across borders
              without restrictive immigration policies. Professionals, skilled
              workers, and students would have expanded opportunities, leading
              to a more dynamic and competitive workforce that drives economic
              growth.
            </p>
          </motion.div>
        </div>

        <div className="mt-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="text-2xl font-semibold text-[var(--color-primary-dark)] mb-6 text-center"
          >
            Leveraging Combined Strengths
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 container mx-auto px-4 py-8">
            {/* Colonne gauche */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="bg-white p-6 shadow-lg border border-gray-200 rounded"
            >
              <p className="mb-6 text-gray-700 leading-relaxed text-base">
                The proposed NAU would capitalize on the unique assets of both
                nations, creating a synergistic partnership poised to thrive on
                the global stage.
              </p>

              <div className="space-y-4">
                <h5 className="text-lg md:text-xl font-semibold  text-center pt-6 text-secondary">
                  Canada’s Abundance of Natural Resources
                </h5>
                <p className="text-gray-700 leading-relaxed text-base">
                  Canada boasts vast natural resources, including oil, natural
                  gas, minerals, and timber. This wealth positions Canada as a
                  key player in global energy and commodities markets, providing
                  a stable supply of essential materials.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold mt-5 text-center  pt-6 text-secondary">
                  Joint Leadership in Artificial Intelligence (AI)
                </h5>

                <div>
                  <h6 className="text-md md:text-lg font-medium text-primary">
                    Canada’s AI Revolution
                  </h6>
                  <p className="text-gray-700 leading-relaxed text-base">
                    Canada has been at the forefront of AI research and
                    development, thanks to groundbreaking work by experts like
                    Geoffrey HinNAUN, Alex Krizhevsky, and Ilya Sutskever. Their
                    creation of AlexNet in 2012 spurred the modern AI boom,
                    making Canadian institutions—such as the Vector Institute in
                    Toronto—global hubs for cutting-edge AI research.
                  </p>
                </div>

                <div>
                  <h6 className="text-md md:text-lg font-medium text-primary">
                    U.S. AI Prowess
                  </h6>
                  <p className="text-gray-700 leading-relaxed text-base">
                    The United States is home to some of the world’s largest
                    tech companies (e.g., Google, Microsoft, Meta, Amazon,
                    OpenAI) and prestigious research universities (e.g., MIT,
                    Stanford, Carnegie Mellon). These institutions and
                    enterprises pour billions into AI development, pushing the
                    boundaries in fields like natural language processing,
                    self-driving vehicles, computer vision and advanced
                    robotics.
                  </p>
                </div>

                <div>
                  <h6 className="text-md md:text-lg font-medium text-primary">
                    Synergistic Advantages
                  </h6>
                  <p className="text-gray-700 leading-relaxed text-base">
                    By uniting Canada’s flourishing AI research industry with
                    the United States’ robust tech ecosystems, the NAU would
                    become an unrivaled AI powerhouse. Crucially, modern AI
                    requires significant electricity to power its computing
                    infrastructure, and Canada’s vast natural resources,
                    including hydro, wind, and other renewable energy sources,
                    provide the reliable, large-scale power essential for AI’s
                    continued growth. This collaboration would attract global
                    talent, accelerate innovation, and shape global AI policy
                    and standards, all while maintaining the sustainable energy
                    backbone necessary for AI to thrive.
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  United States’ Military and Economic Prowess
                </h5>
                <p className="text-gray-700 leading-relaxed text-base">
                  The United States stands as a global military superpower, with
                  unparalleled defense capabilities and strategic influence. Its
                  robust economy, underpinned by technological innovation, a
                  dynamic private sector, and the world’s reserve currency,
                  complements Canada’s resource wealth and AI leadership. This
                  combination would give the NAU formidable economic leverage
                  and enhance collective security.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center pt-6 text-secondary">
                  A Tradition of Global Goodwill
                </h5>
                <p className="text-gray-700 leading-relaxed text-base">
                  Historically, the United States has shown a commitment to
                  global welfare through substantial foreign aid contributions.
                  In fiscal year 2023, the U.S. allocated approximately $71.9
                  billion to foreign assistance, supporting economic
                  development, humanitarian efforts, and health initiatives
                  worldwide. Paired with Canada’s longstanding reputation for
                  peacekeeping and multilateral diplomacy, the NAU would have an
                  amplified capacity for global engagement and humanitarian
                  leadership.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  Agricultural Abundance
                </h5>
                <p className="text-gray-700 leading-relaxed text-base ">
                  Both Canada and the United States are agricultural
                  powerhouses. In 2023, Canada accounted for 16.3% of U.S.
                  agricultural exports and 20.6% of imports, underscoring a
                  vibrant bilateral trade in agricultural products. By pooling
                  resources, technology, and expertise, the NAU could emerge as
                  a global leader in food security, meeting domestic needs while
                  addressing international challenges related to hunger and
                  sustainability.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  Aerospace Leadership
                </h5>
                <h6 className="text-md md:text-lg font-medium  text-primary">
                  A Legacy of Innovation
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Both countries are home to major aerospace manufacturers and
                  innovators. The United States boasts Boeing, Lockheed Martin,
                  Bell Textron, and SpaceX, while Canada is the birthplace of
                  Bombardier and De Havilland. Although Bell is headquartered in
                  the United States, it maintains a significant manufacturing
                  presence in Canada (notably in Mirabel, Quebec), underscoring
                  the deep integration of the North American aerospace industry.
                  These industry giants have a history of producing cutting-edge
                  commercial and military aircraft, a testament to the region’s
                  capacity for aviation excellence.
                </p>
              </div>
            </motion.div>

            {/* Colonne droite */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="bg-white p-6 shadow-lg border border-gray-200 rounded"
            >
              <div className="space-y-4">
                <h6 className="text-md md:text-lg font-medium  text-primary">
                  Space Exploration Collaboration
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  The U.S. National Aeronautics and Space Administration (NASA)
                  and the Canadian Space Agency (CSA) have cooperated for
                  decades, notably on the International Space Station’s robotic
                  systems (the Canadarm). Within an NAU framework, shared R&D
                  programs could accelerate breakthroughs in satellite
                  technology, deep-space missions, and commercial spaceflight.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h6 className="text-md md:text-lg font-medium  text-primary">
                  Economic and Strategic Impact
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  A united aerospace sector would not only bolster economic
                  growth through exports but also ensure mutual security
                  interests are met via advanced surveillance, communication,
                  and defense technologies. This synergy would also spur job
                  creation in engineering, manufacturing, and service sectors.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  Innovation Powerhouse
                </h5>
                <h6 className="text-md md:text-lg font-medium  text-primary">
                  Research and Development (R&D)
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Both countries consistently rank among the top global spenders
                  on R&D. Canada’s government-backed programs and America’s
                  venture-capital-driven tech ecosystem provide fertile ground
                  for breakthroughs in Biotechnology, Clean Energy, Advanced
                  Materials, and Quantum Computing.
                </p>
                <h6 className="mt-4 text-md md:text-lg font-medium text-primary">
                  World-Class Universities and Talent Pool
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  The United States hosts many of the world’s top universities,
                  while Canada’s institutions, such as the University of Toronto
                  and the University of Waterloo, have cultivated strong
                  reputations in AI, Engineering, and Computer Science.
                  Combined, they form an academic juggernaut attracting students
                  and researchers from around the globe.
                </p>
                <h6 className="mt-4 text-md md:text-lg font-medium text-primary">
                  Venture Capital and Startup Ecosystems
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Silicon Valley in the U.S. and emerging tech hubs in Toronto,
                  Montreal, and Vancouver demonstrate a shared entrepreneurial
                  spirit. A merged market would expand funding opportunities and
                  customer bases for startups, accelerating the transition of
                  research breakthroughs into commercial products.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  Integrated Supply Chains and Consumer Markets
                </h5>
                <h6 className="text-md md:text-lg font-medium text-primary">
                  Trade Synergy
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  The U.S. and Canada already share one of the world’s largest
                  and most complex trading relationships, with deeply integrated
                  supply chains, especially in the automotive, aerospace, and
                  agricultural sectors. Harmonizing regulations and eliminating
                  barriers within an NAU would further boost trade efficiency.
                </p>
                <h6 className="mt-4 text-md md:text-lg font-medium text-primary">
                  Large, Prosperous Consumer Base
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Combining populations, over 330 million in the U.S. and nearly
                  40 million in Canada, creates a massive consumer market with
                  high purchasing power. Companies operating within a NAU would
                  benefit from streamlined market access, fostering domestic
                  innovation and attracting foreign investment.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h5 className="text-lg md:text-xl font-semibold text-center  pt-6 text-secondary">
                  Enhanced Diplomatic and Global Influence
                </h5>
                <h6 className="text-md md:text-lg font-medium  text-primary">
                  Shared Values and Alliances
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Both Canada and the United States are members of key
                  international organizations (NATO, G7, Five Eye) and share a
                  commitment to democracy, human rights, and the rule of law. A
                  unified foreign policy approach would significantly amplify
                  diplomatic influence.
                </p>
                <h6 className="mt-4 text-md md:text-lg font-medium  text-primary">
                  Expanded Soft Power
                </h6>
                <p className="text-gray-700 leading-relaxed text-base">
                  Canadian peacekeeping and America’s cultural, technological,
                  and financial clout could merge into a powerful brand of
                  “smart power,” combining humanitarian credibility with
                  strategic reach. This could shape global norms and foster
                  stability in volatile regions.
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="container mx-auto px-4 py-10"
          >
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 text-gray-800 text-base md:text-lg leading-relaxed space-y-8">
              {/* A Unique Opportunity: Smoother Integration */}
              <div>
                <h4 className="text-xl md:text-2xl font-semibold mb-5 text-secondary text-center">
                  A Unique Opportunity: A smooth Integration
                </h4>
                <p>
                  Unlike the European Union, where multiple languages and
                  diverse legal frameworks required extensive alignment efforts,
                  the United States and Canada share a common language and
                  similar social and legal frameworks. This commonality would
                  facilitate smoother and more efficient integration, allowing
                  both nations to enjoy the economic benefits of unity without
                  compromising national identities.
                </p>
              </div>

              {/* Have Your Say */}
              <div>
                <h4 className="text-xl md:text-2xl font-semibold mb-5  text-secondary text-center">
                  Have Your Say: Is the North American Union the Future?
                </h4>
                <p>
                  The concept of a North American Union presents both exciting
                  opportunities and significant challenges. While it promises
                  economic strength, workforce mobility, and market expansion,
                  it also ensures energy security and food stability for North
                  America by leveraging Canada’s vast natural resources and the
                  agricultural power of both nations. Questions remain about its
                  feasibility, political will, and long-term impact on
                  sovereignty. Engaging in this dialogue is crucial as we
                  consider the future of North American collaboration and its
                  potential to reshape our global standing.
                </p>
              </div>

              {/* What Is NAU? */}
              <div>
                <h4 className="flex items-center justify-center text-xl md:text-2xl font-semibold mb-5 text-secondary text-center gap-2">
                  Voice your opinion with <strong>NAU</strong> <TokenImageNAU />
                  ?
                </h4>

                <p>
                  NAU (pronounced like the English word “now”) is more than just
                  another token. It is a symbol of unity and urgency,
                  celebrating the diverse voices that shape North America. We
                  can say yes. We can say no. But ultimately, we must choose
                  now—because our continent’s future can not wait.
                </p>
              </div>

              {/* Where do you stand? */}
              <div>
                <h4 className="text-xl md:text-2xl font-semibold mb-5 text-secondary text-center">
                  Where do you stand?
                </h4>
                <p>
                  Share Your Voice in Real Time, because the Time Is NAU!
                  Feeling curious, excited, or even skeptical about a potential
                  North American Union? Now you can cast your opinion in a fun,
                  community-driven way with NAU Tokens. By minting{" "}
                  <span className="inline-flex items-center gap-1">
                    <strong>NAUY</strong> <TokenImageNAUY />
                  </span>{" "}
                  (“now-ee”) if you’re a ‘yes’ or{" "}
                  <span className="inline-flex items-center gap-1">
                    <strong>NAUN</strong> <TokenImageNAUN />
                  </span>{" "}
                  (“now-en”) if you’re a ‘no’, you add your voice to a living
                  record on the blockchain, secure, anonymous, and free from
                  censorship.
                </p>
              </div>

              {/* Why Join the NAU Movement? */}
              <div>
                <h4 className="text-xl md:text-2xl font-semibold mb-5 text-secondary text-center">
                  Why Join the NAU Movement?
                </h4>
                <p>
                  <strong>Community-Driven & Fun:</strong> NAU is all about
                  sparking friendly debate—no matter your stance. We celebrate
                  every voice as part of a lively North American mosaic.
                  <br />
                  <strong>Heritage & Culture:</strong> By honoring the deep
                  roots of this continent and the diverse cultures that shape
                  it, we celebrate our shared past while forging a collective
                  future.
                  <br />
                  <strong>Immediacy & Action:</strong> It’s called “now” for a
                  reason—our decisions today shape tomorrow’s North America.
                  <br />
                  <strong>Transparent & Secure:</strong> With blockchain
                  technology, your voice remains both permanent and anonymous,
                  fostering trust without sacrificing privacy.
                </p>
              </div>

              {/* We Owe It to Our Shared Heritage */}
              <div>
                <h4 className="text-xl md:text-2xl font-semibold mb-5 text-secondary text-center">
                  We Owe It to Our Shared Heritage
                </h4>
                <p>
                  From the Arctic Tundra to the Sonoran Desert, this continent
                  brims with cultures, languages, and dreams worth protecting,
                  and worth uniting. NAU gives everyone a chance to stand up and
                  be counted, right now.
                </p>
              </div>

              {/* Call to Action */}
              <div>
                <p className="font-medium">
                  So, are you NAUY or NAUN? Whichever path you choose, join the
                  conversation. Together, we can write the next chapter in North
                  America’s story, One Token at a Time.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </BasePage>
  );
};

export default HomePage;
